import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CompanyProductImportApplyMode,
  CompanyProductImportSourceType,
  CompanyProductSettingMode,
  CompanyProductsBrandOption,
  type CompanyProductsCompanyOption,
} from '../companyproductsdialog/companyproductsdialog.component';
import { RewardsAdminService } from '../../../services/rewards-admin/rewards-admin.service';
import { TeamCompaniesService } from '../../../services/teamcompanies/teamcompanies.service';

export interface CompanySnackRewardsDialogResult {
  mode: CompanyProductSettingMode;
  snackRewardIds: string[];
}

export interface CompanySnackRewardsCompanyOption extends CompanyProductsCompanyOption {
  brand?: string;
}

@Component({
    selector: 'app-company-snack-rewards-dialog',
    templateUrl: './company-snack-rewards-dialog.component.html',
    styleUrls: ['./company-snack-rewards-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompanySnackRewardsDialogComponent {
  public mode: CompanyProductSettingMode;
  public rewards: any[];
  public showSelectedOnly = false;
  public importSourceType: CompanyProductImportSourceType;
  public importApplyMode: CompanyProductImportApplyMode = 'replace';
  public selectedBrandId = '';
  public selectedCompanyId = '';
  public importSourceLabel = '';
  public importPreviewLoaded = false;
  public importPreviewApplied = false;
  public importPreviewRewardIds: string[] = [];
  public importPreviewAddedRewardIds: string[] = [];
  public importPreviewOverlappingRewardIds: string[] = [];
  public importPreviewRemovedRewardIds: string[] = [];
  public importPreviewUnavailableRewardIds: string[] = [];
  public importError = '';
  public isImportLoading = false;
  public isImportHelperExpanded = false;
  private customSnackRewardIds: string[];

  constructor(
    public dialogRef: MatDialogRef<CompanySnackRewardsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CompanySnackRewardsDialogModel,
    private rewardsAdminService: RewardsAdminService,
    private teamCompaniesService: TeamCompaniesService,
  ) {
    this.mode = data?.initialMode || 'custom';
    this.rewards = this.normalizeRewards(data?.rewards || []);
    const selectableRewardIds = new Set(this.rewards.map(reward => reward.id));
    this.customSnackRewardIds = this.mode === 'none'
      ? []
      : this.normalizeIds(data?.snackRewardIds || []).filter(rewardId => selectableRewardIds.has(rewardId));
    this.importSourceType = (data?.brandOptions || []).length > 0 ? 'brand' : 'company';
    this.selectedBrandId = data?.initialBrandId || '';
  }

  get displayedRewards(): any[] {
    if (!this.showSelectedOnly) {
      return this.rewards;
    }
    const selectedIds = new Set(this.customSnackRewardIds);
    return this.rewards.filter((reward) => selectedIds.has(reward.id));
  }

  canInherit(): boolean {
    return !!this.data?.canInherit;
  }

  isCustomMode(): boolean {
    return this.mode === 'custom';
  }

  setMode(mode: CompanyProductSettingMode): void {
    if (mode === this.mode) {
      return;
    }

    if (mode === 'custom' && this.mode !== 'custom') {
      const selectableRewardIds = new Set(this.rewards.map(reward => reward.id));
      this.customSnackRewardIds = this.normalizeIds(this.data?.snackRewardIds || [])
        .filter(rewardId => selectableRewardIds.has(rewardId));
    }
    this.mode = mode;
  }

  isSelected(rewardId: string): boolean {
    return this.customSnackRewardIds.includes(rewardId);
  }

  isAllSelected(): boolean {
    return this.isCustomMode()
      && this.rewards.length > 0
      && this.customSnackRewardIds.length === this.rewards.length;
  }

  isSomeSelected(): boolean {
    return this.isCustomMode() && this.customSnackRewardIds.length > 0;
  }

  toggleReward(rewardId: string, checked: boolean): void {
    if (!this.isCustomMode()) {
      return;
    }

    const selected = new Set(this.customSnackRewardIds);
    if (checked) {
      selected.add(rewardId);
    } else {
      selected.delete(rewardId);
    }
    this.customSnackRewardIds = Array.from(selected);
  }

  toggleAllRewards(checked: boolean): void {
    if (!this.isCustomMode()) {
      return;
    }
    this.customSnackRewardIds = checked ? this.rewards.map((reward) => reward.id) : [];
  }

  setShowSelectedOnly(showSelectedOnly: boolean): void {
    this.showSelectedOnly = showSelectedOnly;
  }

  getSelectedCount(): number {
    return this.customSnackRewardIds.length;
  }

  clearSelected(): void {
    this.toggleAllRewards(false);
  }

  hasImportOptions(): boolean {
    return (this.data?.brandOptions || []).length > 0 || (this.data?.companyOptions || []).length > 0;
  }

  toggleImportHelper(): void {
    this.isImportHelperExpanded = !this.isImportHelperExpanded;
  }

  onImportSourceTypeChange(sourceType: CompanyProductImportSourceType): void {
    this.importSourceType = sourceType;
    this.resetImportPreview();
  }

  setImportApplyMode(applyMode: CompanyProductImportApplyMode): void {
    this.importApplyMode = applyMode;
    if (this.importPreviewLoaded) {
      this.buildImportDiff();
    }
  }

  async loadImportPreview(): Promise<void> {
    this.resetImportPreview();
    if (this.importSourceType === 'company') {
      await this.loadCompanyImportPreview();
      return;
    }
    await this.loadBrandImportPreview();
  }

  canApplyImportPreview(): boolean {
    return this.importPreviewLoaded && !this.isImportLoading && !this.importError;
  }

  applyImportPreview(): void {
    if (!this.canApplyImportPreview()) {
      return;
    }

    const currentIds = this.importApplyMode === 'merge' ? this.customSnackRewardIds : [];
    this.customSnackRewardIds = this.normalizeIds([...currentIds, ...this.importPreviewRewardIds]);
    this.mode = 'custom';
    this.importPreviewApplied = true;
  }

  getRewardTitle(reward: any): string {
    const bannerDetails = this.toObject(reward?.bannerDetails);
    return bannerDetails?.title || reward?.name || reward?.id || '';
  }

  getRewardSubtitle(reward: any): string {
    const bannerDetails = this.toObject(reward?.bannerDetails);
    const subtitle = `${bannerDetails?.subtitle || ''}`.replace(/<[^>]*>/g, '').trim();
    if (!subtitle) {
      return reward?.id || '';
    }
    if (!reward?.id) {
      return subtitle;
    }
    return `${subtitle} - ${reward.id}`;
  }

  getRewardLabel(rewardId: string): string {
    const reward = this.rewards.find((candidate) => candidate.id === rewardId);
    return reward ? `${this.getRewardTitle(reward)} (${rewardId})` : rewardId;
  }

  trackReward(_index: number, reward: any): string {
    return reward?.id;
  }

  trackRewardId(_index: number, rewardId: string): string {
    return rewardId;
  }

  okClicked(): void {
    this.dialogRef.close({
      mode: this.mode,
      snackRewardIds: this.mode === 'custom' ? [...this.customSnackRewardIds] : [],
    });
  }

  onDismiss(): void {
    this.dialogRef.close(undefined);
  }

  private async loadBrandImportPreview(): Promise<void> {
    const option = (this.data?.brandOptions || []).find((brand) => brand.id === this.selectedBrandId);
    const brand = `${option?.slug || option?.id || ''}`.trim();
    if (!brand) {
      this.importError = 'Choose a brand first.';
      return;
    }

    this.isImportLoading = true;
    try {
      const rewards = await this.rewardsAdminService.getSnackRewardCandidatesForBrandSource(brand);
      this.importSourceLabel = option?.label || brand;
      this.setImportSourceRewardIds((rewards || []).map((reward) => reward?.id));
    } catch (error: any) {
      this.importError = error?.error?.error || error?.message || 'Unable to load brand snack rewards.';
    } finally {
      this.isImportLoading = false;
    }
  }

  private async loadCompanyImportPreview(): Promise<void> {
    if (!this.selectedCompanyId) {
      this.importError = 'Choose a company first.';
      return;
    }

    this.isImportLoading = true;
    try {
      const company = await this.teamCompaniesService.getCompany(this.selectedCompanyId);
      const resolvedPayrollConfig = this.teamCompaniesService.getResolvedPayrollConfig(company);
      const option = (this.data?.companyOptions || [])
        .find((companyOption) => companyOption.id === this.selectedCompanyId);
      this.importSourceLabel = company?.name || option?.label || 'selected company';

      if (this.hasOwnField(resolvedPayrollConfig, 'snackRewardIds')) {
        this.setImportSourceRewardIds(resolvedPayrollConfig.snackRewardIds || []);
        return;
      }

      const brand = `${option?.brand || company?.snackBrand || ''}`.trim();
      if (!brand) {
        this.importError = 'Unable to determine the source company snack reward brand.';
        return;
      }
      const rewards = await this.rewardsAdminService.getSnackRewardCandidates(brand, company.id || this.selectedCompanyId);
      this.setImportSourceRewardIds((rewards || []).map((reward) => reward?.id));
    } catch (error: any) {
      this.importError = error?.error?.error || error?.message || 'Unable to load company snack rewards.';
    } finally {
      this.isImportLoading = false;
    }
  }

  private setImportSourceRewardIds(sourceRewardIds: any[]): void {
    const selectableIds = new Set(this.rewards.map((reward) => reward.id));
    const uniqueSourceIds = this.normalizeIds(sourceRewardIds || []);
    this.importPreviewRewardIds = uniqueSourceIds.filter((rewardId) => selectableIds.has(rewardId));
    this.importPreviewUnavailableRewardIds = uniqueSourceIds.filter((rewardId) => !selectableIds.has(rewardId));
    this.importPreviewLoaded = true;
    this.buildImportDiff();
  }

  private buildImportDiff(): void {
    const currentIds = new Set(this.customSnackRewardIds);
    const sourceIds = new Set(this.importPreviewRewardIds);
    this.importPreviewAddedRewardIds = this.importPreviewRewardIds.filter((rewardId) => !currentIds.has(rewardId));
    this.importPreviewOverlappingRewardIds = this.importPreviewRewardIds.filter((rewardId) => currentIds.has(rewardId));
    this.importPreviewRemovedRewardIds = this.importApplyMode === 'replace'
      ? this.customSnackRewardIds.filter((rewardId) => !sourceIds.has(rewardId))
      : [];
  }

  private resetImportPreview(): void {
    this.importSourceLabel = '';
    this.importPreviewLoaded = false;
    this.importPreviewApplied = false;
    this.importPreviewRewardIds = [];
    this.importPreviewAddedRewardIds = [];
    this.importPreviewOverlappingRewardIds = [];
    this.importPreviewRemovedRewardIds = [];
    this.importPreviewUnavailableRewardIds = [];
    this.importError = '';
  }

  private normalizeRewards(rewards: any[]): any[] {
    const seenIds = new Set<string>();
    return (Array.isArray(rewards) ? rewards : [])
      .filter((reward) => {
        if (!reward?.id || this.toObject(reward?.rewardDetails)?.clo || seenIds.has(reward.id)) {
          return false;
        }
        seenIds.add(reward.id);
        return true;
      })
      .sort((a, b) => this.getRewardTitle(a).localeCompare(this.getRewardTitle(b)));
  }

  private normalizeIds(values: any[]): string[] {
    return Array.from(new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => `${value || ''}`.trim())
        .filter(Boolean)
    ));
  }

  private hasOwnField(value: any, field: string): boolean {
    return !!value && Object.prototype.hasOwnProperty.call(value, field);
  }

  private toObject(value: any): any {
    if (!value) {
      return {};
    }
    if (typeof value === 'object') {
      return value;
    }
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return {};
  }
}

export class CompanySnackRewardsDialogModel {
  constructor(
    public rewards: any[],
    public snackRewardIds: string[],
    public companyName: string,
    public brand: string,
    public canInherit: boolean,
    public parentCompanyName: string,
    public initialMode: CompanyProductSettingMode,
    public brandOptions: CompanyProductsBrandOption[] = [],
    public companyOptions: CompanySnackRewardsCompanyOption[] = [],
    public initialBrandId: string = '',
  ) {
  }
}
